/**
 * Client-side PO reconciliation conflict checks (Match modal warnings).
 * Server re-validates on save via express invoicePoReconciliationConflict.
 *
 * When the current invoice has a supplier, only warn for the same supplier —
 * PO numbers are not globally unique across suppliers.
 * When current supplier is unknown, keep legacy PO-only matching.
 */

const CLOSED_LIFECYCLES = new Set(['reconciled', 'sentToXero'])

function normalisePo(value) {
  return String(value || '').trim()
}

function normaliseSupplierId(value) {
  if (value == null || value === '') return ''
  if (typeof value === 'object') {
    const id = value._id ?? value.id
    if (id != null && id !== '') return String(id).trim()
  }
  return String(value).trim()
}

/** Table rows use supplierId; raw invoice docs use supplier. */
function invoiceSupplierId(invoice = {}) {
  return (
    normaliseSupplierId(invoice.supplier) ||
    normaliseSupplierId(invoice.supplierId)
  )
}

function isSameSupplierScope(currentSupplierId, invoice = {}) {
  const current = normaliseSupplierId(currentSupplierId)
  if (!current) return true

  const other = invoiceSupplierId(invoice)
  return Boolean(other) && other === current
}

function poMatch(left, right) {
  const a = normalisePo(left).toUpperCase()
  const b = normalisePo(right).toUpperCase()
  return Boolean(a && b && a === b)
}

function hasMultiPoAllocations(invoice = {}) {
  return (
    Array.isArray(invoice.poAllocations) && invoice.poAllocations.length >= 2
  )
}

function invoicePoNumbers(invoice = {}) {
  return [
    invoice.purchaseOrderNumber,
    ...(Array.isArray(invoice.matchedPurchaseOrderNumbers)
      ? invoice.matchedPurchaseOrderNumbers
      : []),
  ]
    .map(normalisePo)
    .filter(Boolean)
}

function poIsReconciledOnInvoice(invoice = {}, targetPo) {
  const po = normalisePo(targetPo)
  if (!po) return false

  for (const row of invoice.poAllocations || []) {
    if (poMatch(row?.purchaseOrderNumber, po) && row?.reconciledAt) {
      return true
    }
  }

  const lifecycle = String(invoice.status || '').trim()
  if (!CLOSED_LIFECYCLES.has(lifecycle)) return false
  if (hasMultiPoAllocations(invoice)) {
    return (invoice.poAllocations || []).some(
      row => poMatch(row?.purchaseOrderNumber, po) && row?.reconciledAt,
    )
  }

  return invoicePoNumbers(invoice).some(value => poMatch(value, po))
}

export function findPoReconciliationConflictInLibrary({
  purchaseOrderNumber,
  currentInvoiceId,
  supplierId,
  invoiceRows = [],
}) {
  const po = normalisePo(purchaseOrderNumber)
  if (!po) return null

  for (const invoice of invoiceRows) {
    if (
      currentInvoiceId &&
      String(invoice?.id || '') === String(currentInvoiceId)
    ) {
      continue
    }
    if (invoice?.isArchived) continue
    if (!isSameSupplierScope(supplierId, invoice)) continue
    if (!poIsReconciledOnInvoice(invoice, po)) continue

    const invoiceNumber =
      String(invoice.invoiceNumber || '').trim() ||
      String(invoice.fileName || '').trim() ||
      'another invoice'

    return {
      purchaseOrderNumber: po,
      conflictingInvoiceId: String(invoice.id || ''),
      conflictingInvoiceNumber: invoiceNumber,
      message: `This order is already reconciled against Invoice ${invoiceNumber}.`,
    }
  }

  return null
}

export function getPoReconciliationConflictWarning(conflict) {
  return conflict?.message || null
}
